import { useMemo, useCallback } from "react";
import { useClientPortalSelector, selectBooking } from "../../../../redux";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { useRelatedClients } from "./useRelatedClients";
import { applyDiscount, discountText } from "../../AppointmentBooking.helpers";
import { AppointmentBookingClientModel, DiscountType } from "types/generated/models";

export interface UseClientDiscountResult {
    /** The client model with discount info, derived from confirmation or logged-in user */
    selectedClient: AppointmentBookingClientModel | undefined;

    /** Raw discount value - percentage as decimal (0.1 = 10%) or fixed currency amount */
    discount: number | undefined;

    /** Whether discount is percentage or currency */
    discountType: DiscountType | undefined;

    /** Formatted display text (e.g., "Discount Applied: 10%"), null if no discount */
    discountContent: string | null;

    /** Memoized function to apply discount to an amount */
    applyDiscountToAmount: (amount: number) => number;
}

/**
 * Hook to manage client discount logic in the booking confirmation flow.
 *
 * Derives the selected client from either:
 * 1. The explicitly selected client in confirmation state
 * 2. The logged-in user's client record from related clients
 */
export const useClientDiscount = (): UseClientDiscountResult => {
    const { confirmation } = useClientPortalSelector(selectBooking);
    const {
        clientPortalConfig: { currencySymbol, userDetails },
    } = useClientPortalConfigContext();
    const { relatedClients } = useRelatedClients();

    const selectedClient = useMemo(() => {
        if (confirmation.data?.client) {
            return confirmation.data.client;
        }
        return relatedClients?.find((c) => c.id === userDetails?.clientId);
    }, [confirmation.data?.client, relatedClients, userDetails?.clientId]);

    const discount = selectedClient?.discount;
    const discountType = selectedClient?.discountType;

    const discountContent = useMemo(() => {
        if (!discount || discount <= 0) {
            return null;
        }
        return `Discount Applied: ${discountText(discount, discountType, currencySymbol)}`;
    }, [discount, discountType, currencySymbol]);

    const applyDiscountToAmount = useCallback(
        (amount: number) => applyDiscount(amount, discount, discountType),
        [discount, discountType]
    );

    return {
        selectedClient,
        discount,
        discountType,
        discountContent,
        applyDiscountToAmount,
    };
};
